import { AvailabilityMethod } from 'generated-graphql/graphql';
import { APICalendarMeetingTemplateSchema } from '../api/useCreateCalendarMeetingTemplate';
import {
  CalendarMeetingTemplateFormData,
  MeetingLocation,
  MeetingUnit
} from '../constants/calendarMeetingTemplateSchema';
import { UseUserCalendarReturnData } from '../hooks/useUserCalendar';
import { findCalendarByEmail } from './findCalendarByEmail';
import { DEFAULT_REQUIRED_HOURS } from 'modules/calendar/constants/requiredHoursDefault';

export const mapFetchCalendarMeetingTemplate = (
  data: APICalendarMeetingTemplateSchema,
  userCalendars: UseUserCalendarReturnData
): CalendarMeetingTemplateFormData => {
  const mappedUserCalendars = data.participants.reduce(
    (acc, item) => {
      const id = findCalendarByEmail(userCalendars, item.email)?.id;
      if (!id) return acc;
      if (item.isOrganizer)
        return { ownerId: id, participants: acc.participants };
      return { ownerId: acc.ownerId, participants: [...acc.participants, id] };
    },
    {
      ownerId: '',
      participants: []
    } as {
      ownerId: string;
      participants: string[];
    }
  );

  const dailyAvailability = Object.entries(data.availability).reduce(
    (acc, [key, value]) => {
      const dayAvailability = (
        value as {
          start: string;
          end: string;
        }[]
      )?.map((timeSlot) => {
        return [
          new Date(`2021-01-01T${timeSlot.start}`),
          new Date(`2021-01-01T${timeSlot.end}`)
        ];
      });

      const dayKey = '';
      switch (key) {
        case '0':
          return {
            ...acc,
            sundayAvailability: dayAvailability
          };
        case '1':
          return {
            ...acc,
            mondayAvailability: dayAvailability
          };
        case '2':
          return {
            ...acc,
            tuesdayAvailability: dayAvailability
          };
        case '3':
          return {
            ...acc,
            wednesdayAvailability: dayAvailability
          };
        case '4':
          return {
            ...acc,
            thursdayAvailability: dayAvailability
          };
        case '5':
          return {
            ...acc,
            fridayAvailability: dayAvailability
          };
        case '6':
          return {
            ...acc,
            saturdayAvailability: dayAvailability
          };
      }

      return {
        ...acc,
        [dayKey]: dayAvailability
      };
    },
    {} as {
      mondayAvailability: Date[][];
      tuesdayAvailability: Date[][];
      wednesdayAvailability: Date[][];
      thursdayAvailability: Date[][];
      fridayAvailability: Date[][];
      saturdayAvailability: Date[][];
      sundayAvailability: Date[][];
    }
  );

  const mappedData: CalendarMeetingTemplateFormData = {
    title: data.title,
    description: data.description,
    location: data.videoConference
      ? MeetingLocation.Video
      : MeetingLocation.InPerson,
    durationValue: data.durationMinutes,
    durationUnit: MeetingUnit.Minutes,
    schedulingMethod: data.availabilityMethod ?? AvailabilityMethod.Collective,
    owner: mappedUserCalendars.ownerId,
    participants: mappedUserCalendars.participants,
    mondayAvailability: dailyAvailability.mondayAvailability,
    tuesdayAvailability: dailyAvailability.tuesdayAvailability,
    wednesdayAvailability: dailyAvailability.wednesdayAvailability,
    thursdayAvailability: dailyAvailability.thursdayAvailability,
    fridayAvailability: dailyAvailability.fridayAvailability,
    saturdayAvailability: dailyAvailability.saturdayAvailability,
    sundayAvailability: dailyAvailability.sundayAvailability,
    timeZone: data.timezone,
    sendMeetingReminderValue: data.sendMeetingReminder,
    sendMeetingReminderUnit: MeetingUnit.Minutes,
    bufferTimeValue: data.buffer,
    bufferTimeUnit: MeetingUnit.Minutes,
    noticePeriodValue: data.noticePeriodMinutes,
    noticePeriodUnit: MeetingUnit.Minutes,
    bookBeforeValue: data.availableDaysInTheFuture,
    locationDetails: data.videoConference ? undefined : data.location,
    // Seeded so the field isn't empty on a template that never stored one -
    // whether it actually gets sent is decided by dirtiness, not by this value.
    requiredHours:
      data.availabilityOutreachSettings?.requiredHours ?? DEFAULT_REQUIRED_HOURS
  };

  return mappedData;
};
